import React, { useState, useEffect } from 'react';
import { Box, Typography, List, ListItem, ListItemButton, ListItemContent, Divider, Chip, Button, IconButton } from '@mui/joy';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';
import { useProfile } from '../hooks/useProfile';
import { IconWrapper } from './IconWrapper';
import { isSignedIn } from '../utils/graphService';
import { ledgerService } from '../modules/ledger/ledger.service';
import {
  FaWindowMaximize,
  FaRobot,
  FaChartBar,
  FaShoppingCart,
  FaFolder,
  FaCog,
  FaUser,
  FaEnvelope,
  FaBell,
  FaSignOutAlt,
  FaCalendar,
  FaChevronDown,
  FaChevronRight,
  FaPlug,
  FaFileInvoice,
  FaIdCard,
  FaMobileAlt,
  FaMagic,
  FaAddressBook,
  FaImage,
  FaLayerGroup,
  FaMinus,
  FaFlask,
  FaList,
  FaClipboardList,
  FaPhone,
  FaUsers,
  FaBook,
  FaShuttleVan,
  FaIndustry,
} from 'react-icons/fa';

interface SidebarProps {
  onClose?: () => void;
  /** Desktop-only: collapse the nav into a slim rail. */
  onCollapse?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onClose, onCollapse }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useSupabaseAuth();
  const { profile, isSiteCrew, isSupplier } = useProfile();
  const [showSignOut, setShowSignOut] = React.useState(false);
  const [msalConnected, setMsalConnected] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(false);
  const [canSeeLedger, setCanSeeLedger] = useState(false);

  useEffect(() => {
    if (!user) {
      setCanSeeLedger(false);
      return;
    }
    let cancelled = false;
    void ledgerService.canAccess().then((allowed) => {
      if (!cancelled) setCanSeeLedger(allowed);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [expandedNav, setExpandedNav] = useState<Record<string, boolean>>(() => ({
    '/workspaces': location.pathname.startsWith('/workspaces')
      || /\/projects\/[^/]+\/workspace/.test(location.pathname),
  }));

  useEffect(() => {
    if (location.pathname.startsWith('/admin') || location.pathname.startsWith('/door-oracle')) {
      setAdminExpanded(true);
    }
    if (location.pathname.startsWith('/workspaces') || /\/projects\/[^/]+\/workspace/.test(location.pathname)) {
      setExpandedNav((prev) => (prev['/workspaces'] ? prev : { ...prev, '/workspaces': true }));
    }
  }, [location.pathname]);

  // Check MSAL connection status
  useEffect(() => {
    if (isSiteCrew || isSupplier) {
      setMsalConnected(false);
      return;
    }
    const checkMsalStatus = async () => {
      try {
        const connected = await isSignedIn();
        setMsalConnected(connected);
      } catch (error) {
        console.error('Error checking MSAL status:', error);
        setMsalConnected(false);
      }
    };

    // Check immediately on mount
    checkMsalStatus();
    
    // Check status every 10 seconds (more frequent)
    const interval = setInterval(checkMsalStatus, 10000);
    
    // Also check when the page becomes visible (user switches back to tab)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        checkMsalStatus();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isSiteCrew, isSupplier]);

  const topItems = [
    { label: 'Dashboard', icon: FaChartBar, path: '/dashboard', match: (pathname: string) => pathname === '/' || pathname === '/dashboard' || pathname.startsWith('/dashboard/') },
    {
      label: 'Workspaces',
      icon: FaLayerGroup,
      path: '/workspaces',
      match: (pathname: string) => pathname === '/workspaces' || pathname.startsWith('/workspaces/') || /\/projects\/[^/]+\/workspace/.test(pathname),
      children: [
        {
          label: 'List',
          icon: FaList,
          path: '/workspaces/list',
          match: (pathname: string) => pathname === '/workspaces/list' || pathname.startsWith('/workspaces/list/'),
        },
        {
          label: 'Job Cards',
          icon: FaIdCard,
          path: '/workspaces/job-cards',
          match: (pathname: string) => pathname === '/workspaces/job-cards',
        },
        {
          label: "Fitter's view",
          icon: FaMobileAlt,
          path: '/workspaces/fitter-view',
          match: (pathname: string) => pathname === '/workspaces/fitter-view',
        },
        {
          label: 'Job Records',
          icon: FaClipboardList,
          path: '/workspaces/job-records',
          match: (pathname: string) => pathname === '/workspaces/job-records' || pathname.startsWith('/workspaces/job-records/'),
        },
        {
          label: 'Production Dates',
          icon: FaIndustry,
          path: '/workspaces/production-dates',
          match: (pathname: string) => pathname === '/workspaces/production-dates',
        },
      ],
    },
  ];

  const jobItems = [
    // /docs stays routed; hidden from staff nav. Document-role users still need the link.
    ...(profile?.role === 'document'
      ? [{
          label: 'Documents',
          icon: FaFolder,
          path: '/docs',
          match: (pathname: string) => pathname === '/docs' || pathname.startsWith('/docs/'),
        }]
      : []),
    { label: 'Revenue Calendar', icon: FaChartBar, path: '/revenue-calendar' },
    { label: 'Link Outlook Events', icon: FaCalendar, path: '/work-calendar' },
    { label: 'Fleet Driver Policy', icon: FaShuttleVan, path: '/fleet-policy' },
    { label: 'Kanban', icon: FaRobot, path: '/jarvis/kanban' },
  ];

  const marketingItems = [
    { label: 'Leads', icon: FaAddressBook, path: '/leads' },
    { label: 'Image Purify', icon: FaImage, path: '/jarvis/image-purify' },
  ];

  const toolsItems = [
    { label: 'Window Generator', icon: FaMagic, path: '/window-generator' },
    { label: 'Fenestration Query Tool', icon: FaWindowMaximize, path: '/fenestration' },
  ];

  const financeItems = [
    { label: 'Invoicing', icon: FaFileInvoice, path: '/invoicing' },
    { label: 'Bills', icon: FaFolder, path: '/bills' },
    { label: 'Expenses', icon: FaShoppingCart, path: '/expenses' },
    ...(canSeeLedger ? [{ label: 'Ledger', icon: FaBook, path: '/ledger' }] : []),
    { label: 'CIS Certificates', icon: FaIdCard, path: '/cis-certificates' },
    { label: 'Subcontractors', icon: FaUsers, path: '/subcontractors' },
    ...(profile?.role === 'admin'
      ? [{ label: 'Pricing Engine', icon: FaChartBar, path: '/jarvis/pricing-engine' }]
      : []),
  ];

  const telephonyItems = [
    { label: 'Directory', icon: FaFolder, path: '/directory' },
    { label: 'Call Recordings', icon: FaPhone, path: '/call-recordings', match: (pathname: string) => pathname === '/call-recordings' || pathname.startsWith('/call-recordings/') },
    { label: 'Contacts', icon: FaUsers, path: '/contacts' },
  ];

  const adminPanelItems = [
    { label: 'Pricing', icon: FaChartBar, path: '/admin?tab=pricing' },
    { label: 'Suppliers', icon: FaCog, path: '/admin?tab=suppliers' },
    { label: 'Email Templates', icon: FaEnvelope, path: '/admin?tab=email-templates' },
    { label: 'Activity', icon: FaBell, path: '/admin?tab=email-activity' },
    { label: 'Change Log', icon: FaFolder, path: '/admin?tab=change-log' },
    { label: 'Event Manager', icon: FaCalendar, path: '/admin?tab=event-manager' },
    { label: 'Notifications', icon: FaEnvelope, path: '/admin?tab=notifications' },
    { label: 'Users', icon: FaUser, path: '/admin?tab=users' },
    { label: 'Organisations', icon: FaIdCard, path: '/admin?tab=organisations' },
    { label: 'PDF Export', icon: FaFolder, path: '/admin?tab=pdf-export' },
    { label: 'Appearance', icon: FaMagic, path: '/admin?tab=appearance' },
    { label: 'Consumer Contracts', icon: FaFolder, path: '/admin?tab=consumer-contracts' },
    { label: 'Survey Integrity', icon: FaLayerGroup, path: '/admin?tab=survey-integrity' },
    { label: 'FreeAgent', icon: FaPlug, path: '/admin?tab=freeagent' },
    { label: 'Scenario engine', icon: FaFlask, path: '/admin?tab=vd-scenarios' },
    { label: 'Door Oracle', icon: FaWindowMaximize, path: '/door-oracle' },
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleUserClick = () => {
    console.log('User clicked!');
    setShowSignOut(!showSignOut);
  };

  const handleAdminClick = () => {
    setAdminExpanded(!adminExpanded);
    if (!adminExpanded) {
      navigate('/admin');
    }
  };

  const isAdminPath = (path: string) => {
    return (
      path.startsWith('/admin') ||
      path.startsWith('/door-oracle') ||
      adminPanelItems.some((item) => path.includes(item.path.split('=')[1]))
    );
  };

  const navItemSx = {
    color: 'var(--jarvis-nav-text)',
    textDecoration: 'none',
    transition: 'background-color 120ms ease, color 120ms ease',
    '&:hover': {
      backgroundColor: 'var(--jarvis-nav-hover-bg)',
      color: 'var(--jarvis-nav-hover-text)',
    },
    '&.Mui-selected': {
      backgroundColor: 'var(--jarvis-nav-selected-bg)',
      color: 'var(--jarvis-nav-selected-text)',
      '&:hover': {
        backgroundColor: 'var(--jarvis-nav-selected-bg)',
        color: 'var(--jarvis-nav-selected-text)',
      },
    },
  } as const;

  const navLabelSx = {
    fontFamily: 'var(--jarvis-font-family)',
    fontWeight: 600,
    color: 'inherit',
  } as const;

  const sectionHeaderSx = {
    fontFamily: 'var(--jarvis-font-family)',
    fontWeight: 700,
    fontSize: '0.7rem',
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    color: 'var(--jarvis-nav-muted-text)',
    px: 1,
    pt: 0.5,
    pb: 0.75,
  };

  type NavItem = {
    label: string;
    icon: typeof FaChartBar;
    path: string;
    match?: (pathname: string) => boolean;
    children?: NavItem[];
  };

  const isItemSelected = (item: NavItem) => (
    item.match ? item.match(location.pathname) : location.pathname === item.path || location.pathname.startsWith(`${item.path}/`)
  );

  const renderNavList = (items: NavItem[], opts?: { documentOnlyPaths?: string[] }) => {
    const filtered = items.filter((item) => {
      if (profile?.role !== 'document') return true;
      return (opts?.documentOnlyPaths || []).includes(item.path);
    });
    if (filtered.length === 0) return null;
    return (
      <List size="sm" sx={{ '--ListItem-radius': '8px', '--List-gap': '4px', px: 2 }}>
        {filtered.map((item) => {
          const hasChildren = !!item.children?.length;
          const expanded = hasChildren && !!expandedNav[item.path];
          return (
            <React.Fragment key={item.path}>
              <ListItem>
                <ListItemButton
                  component={Link}
                  to={item.path}
                  selected={isItemSelected(item)}
                  onClick={() => {
                    if (hasChildren) {
                      setExpandedNav((prev) => ({ ...prev, [item.path]: true }));
                    }
                    onClose?.();
                  }}
                  sx={navItemSx}
                >
                  <Box sx={{ mr: 1, color: 'inherit', display: 'flex' }}>
                    <IconWrapper icon={item.icon} />
                  </Box>
                  <ListItemContent>
                    <Typography level="body-sm" sx={navLabelSx}>{item.label}</Typography>
                  </ListItemContent>
                  {hasChildren && (
                    <Box
                      component="span"
                      role="button"
                      aria-label={expanded ? `Collapse ${item.label}` : `Expand ${item.label}`}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setExpandedNav((prev) => ({ ...prev, [item.path]: !prev[item.path] }));
                      }}
                      sx={{ color: 'inherit', display: 'flex' }}
                    >
                      <IconWrapper icon={expanded ? FaChevronDown : FaChevronRight} />
                    </Box>
                  )}
                </ListItemButton>
              </ListItem>
              {expanded && item.children && (
                <List size="sm" sx={{ '--ListItem-radius': '8px', '--List-gap': '2px', pl: 4 }}>
                  {item.children.map((child) => (
                    <ListItem key={child.path}>
                      <ListItemButton
                        component={Link}
                        to={child.path}
                        selected={isItemSelected(child)}
                        onClick={() => { onClose?.(); }}
                        sx={{ ...navItemSx, fontSize: '0.875rem' }}
                      >
                        <Box sx={{ mr: 1, color: 'inherit', display: 'flex' }}>
                          <IconWrapper icon={child.icon} />
                        </Box>
                        <ListItemContent>
                          <Typography level="body-sm" sx={{ ...navLabelSx, fontWeight: 500 }}>{child.label}</Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </React.Fragment>
          );
        })}
      </List>
    );
  };

  const renderSection = (title: string | null, items: NavItem[], opts?: { documentOnlyPaths?: string[] }) => {
    const list = renderNavList(items, opts);
    if (!list) return null;
    return (
      <>
        <Divider sx={{ my: 2, borderColor: 'var(--jarvis-nav-border)' }} />
        {title ? (
          <Typography level="body-xs" sx={{ ...sectionHeaderSx, px: 3 }}>
            {title}
          </Typography>
        ) : null}
        {list}
      </>
    );
  };

  return (
    <Box
      sx={{
        width: { xs: '280px', lg: '232px' },
        height: '100vh',
        backgroundColor: 'var(--jarvis-nav-bg)',
        color: 'var(--jarvis-nav-text)',
        borderRight: '1px solid',
        borderColor: 'var(--jarvis-nav-border)',
        display: 'flex',
        flexDirection: 'column',
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 1200,
        // Mobile-friendly height adjustments
        '@media (max-width: 768px)': {
          height: '100dvh', // Use dynamic viewport height for mobile
          maxHeight: '100vh', // Fallback
        },
      }}
    >
      {/* Logo */}
      <Box
        sx={{
          px: 2, py: 1.75,
          borderBottom: '1px solid',
          borderColor: 'var(--jarvis-nav-border)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          component="img"
          src="/images/brand/jarvis-logo.png"
          alt="JARVIS"
          sx={{
            display: 'block',
            flex: 1,
            minWidth: 0,
            width: '100%',
            maxWidth: 150,
            height: 'auto',
            borderRadius: 'sm',
          }}
        />
        {onCollapse && (
          <IconButton
            size="sm"
            variant="plain"
            color="neutral"
            aria-label="Collapse navigation"
            onClick={onCollapse}
            sx={{
              display: { xs: 'none', lg: 'inline-flex' },
              flexShrink: 0,
              color: 'var(--jarvis-nav-text)',
              '&:hover': {
                bgcolor: 'var(--jarvis-nav-hover-bg)',
                color: 'var(--jarvis-nav-hover-text)',
              },
            }}
          >
            <IconWrapper icon={FaMinus} />
          </IconButton>
        )}
      </Box>

      {/* Navigation Items */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'auto',
        scrollbarWidth: 'thin',
        scrollbarColor: 'var(--jarvis-nav-scrollbar-thumb) var(--jarvis-nav-scrollbar-track)',
        '&::-webkit-scrollbar': {
          width: 8,
        },
        '&::-webkit-scrollbar-track': {
          background: 'var(--jarvis-nav-scrollbar-track)',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'var(--jarvis-nav-scrollbar-thumb)',
          borderRadius: 4,
        },
        '&::-webkit-scrollbar-thumb:hover': {
          background: 'var(--jarvis-nav-scrollbar-thumb-hover)',
        },
        // Ensure proper scrolling on mobile
        '@media (max-width: 768px)': {
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
        }
      }}>
        {(isSiteCrew || isSupplier) ? (
          <List size="sm" sx={{ '--ListItem-radius': '8px', '--List-gap': '4px', px: 2 }}>
            {isSiteCrew && (
              <>
              <ListItem>
                <ListItemButton
                  component={Link}
                  to="/job-records"
                  selected={location.pathname.startsWith('/job-records')}
                  onClick={() => { onClose?.(); }}
                  sx={navItemSx}
                >
                  <Box sx={{ mr: 1, color: 'inherit', display: 'flex' }}>
                    <IconWrapper icon={FaFileInvoice} />
                  </Box>
                  <ListItemContent>
                    <Typography level="body-sm" sx={navLabelSx}>
                      Fitter Dashboard
                    </Typography>
                  </ListItemContent>
                </ListItemButton>
              </ListItem>
              <ListItem>
                <ListItemButton
                  component={Link}
                  to="/cis-certificates"
                  selected={location.pathname.startsWith('/cis-certificates')}
                  onClick={() => { onClose?.(); }}
                  sx={navItemSx}
                >
                  <Box sx={{ mr: 1, color: 'inherit', display: 'flex' }}>
                    <IconWrapper icon={FaIdCard} />
                  </Box>
                  <ListItemContent>
                    <Typography level="body-sm" sx={navLabelSx}>
                      CIS Certificates
                    </Typography>
                  </ListItemContent>
                </ListItemButton>
              </ListItem>
              <ListItem>
                <ListItemButton
                  component={Link}
                  to="/fleet-policy"
                  selected={location.pathname.startsWith('/fleet-policy')}
                  onClick={() => { onClose?.(); }}
                  sx={navItemSx}
                >
                  <Box sx={{ mr: 1, color: 'inherit', display: 'flex' }}>
                    <IconWrapper icon={FaShuttleVan} />
                  </Box>
                  <ListItemContent>
                    <Typography level="body-sm" sx={navLabelSx}>
                      Fleet Driver Policy
                    </Typography>
                  </ListItemContent>
                </ListItemButton>
              </ListItem>
              </>
            )}
          </List>
        ) : (
        <>
        {renderNavList(topItems)}
        {renderSection('Telephony', telephonyItems)}
        {renderSection('Job', jobItems, { documentOnlyPaths: ['/docs'] })}
        {renderSection('Marketing & Dev', marketingItems)}
        {renderSection('Fenestration', toolsItems)}
        {renderSection('Finance', financeItems)}

        {/* Admin Panel Section - Expandable */}
        {profile?.role !== 'document' && (
          <>
        <Divider sx={{ my: 2, borderColor: 'var(--jarvis-nav-border)' }} />
        
        <List size="sm" sx={{ '--ListItem-radius': '8px', '--List-gap': '4px', px: 2 }}>
          <ListItem>
            <ListItemButton
              component={Link}
              to="/admin"
              selected={isAdminPath(location.pathname)}
              onClick={() => {
                setAdminExpanded(!adminExpanded);
                onClose?.();
              }}
              sx={navItemSx}
            >
              <Box sx={{ mr: 1, color: 'inherit', display: 'flex' }}>
                <IconWrapper icon={FaCog} />
              </Box>
              <ListItemContent>
                <Typography level="body-sm" sx={navLabelSx}>Admin Panel</Typography>
              </ListItemContent>
              <Box sx={{ color: 'inherit', display: 'flex' }}>
                <IconWrapper icon={adminExpanded ? FaChevronDown : FaChevronRight} />
              </Box>
            </ListItemButton>
          </ListItem>
          
          {adminExpanded && (
            <List size="sm" sx={{ '--ListItem-radius': '8px', '--List-gap': '2px', pl: 4 }}>
              {adminPanelItems.map((item) => (
                <ListItem key={item.path}>
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    selected={
                      item.path.includes('?')
                        ? location.search.includes(item.path.split('=')[1] || '')
                        : location.pathname === item.path
                    }
                    onClick={() => {
                      onClose?.();
                    }}
                    sx={{ ...navItemSx, fontSize: '0.875rem' }}
                  >
                    <Box sx={{ mr: 1, color: 'inherit', display: 'flex' }}>
                      <IconWrapper icon={item.icon} />
                    </Box>
                    <ListItemContent>
                      <Typography level="body-sm" sx={{ ...navLabelSx, fontWeight: 500 }}>{item.label}</Typography>
                    </ListItemContent>
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </List>
          </>
        )}
        </>
        )}
      </Box>

      {/* User Info at Bottom */}
      <Box sx={{ 
        p: 2, 
        borderTop: '1px solid', 
        borderColor: 'var(--jarvis-nav-border)',
        // Ensure bottom content is accessible on mobile
        '@media (max-width: 768px)': {
          pb: 3, // Extra bottom padding for mobile
        }
      }}>
        <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
          <Chip
            size="sm"
            variant="solid"
            color="success"
            startDecorator={<IconWrapper icon={FaUser} />}
            sx={{ 
              justifyContent: 'flex-start',
              cursor: 'pointer',
              bgcolor: '#1F7A1F',
              color: '#FFFFFF',
              '&:hover': {
                bgcolor: '#196619',
              },
            }}
            onClick={handleUserClick}
          >
            <Typography level="body-sm" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, color: 'inherit' }}>
              {user?.email || profile?.full_name || 'User'}
            </Typography>
          </Chip>
          {!isSiteCrew && !isSupplier && (
          <Chip
            component="a"
            href="https://jarvis.keystonepropertysouth.co.uk/auth-test"
            target="_blank"
            rel="noopener noreferrer"
            size="sm"
            variant="solid"
            color={msalConnected ? 'success' : 'danger'}
            startDecorator={<IconWrapper icon={FaEnvelope} />}
            sx={{
              fontSize: '0.75rem',
              textDecoration: 'none',
              bgcolor: msalConnected ? '#1F7A1F' : '#C41C1C',
              color: '#FFFFFF',
              '&:hover': {
                bgcolor: msalConnected ? '#196619' : '#A81818',
              },
            }}
          >
            <Typography level="body-sm" sx={{ fontFamily: 'Poppins, sans-serif', fontWeight: 600, color: 'inherit' }}>
              Microsoft Services
            </Typography>
          </Chip>
          )}
        </Box>

        {showSignOut && (
          <Box sx={{ mt: 1 }}>
            <Button
              size="sm"
              variant="outlined"
              color="danger"
              startDecorator={<IconWrapper icon={FaSignOutAlt} />}
              onClick={handleSignOut}
              sx={{ width: '100%' }}
            >
              Sign Out
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Sidebar; 